import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Search, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, X } from 'lucide-react';

const RANK_BORDER = { normal:'#93c5fd', silver:'#cbd5e1', gold:'#fbbf24', platinum:'#1f2937', blacklist:'#f87171', new:'#4ade80' };
function getBorderColor(b) {
  if (b.customer_is_blacklisted) return RANK_BORDER.blacklist;
  if (b.customer_ranking && b.customer_ranking !== 'normal') return RANK_BORDER[b.customer_ranking] || RANK_BORDER.normal;
  if (b.customer_is_new) return RANK_BORDER.new;
  return RANK_BORDER.normal;
}
function copyText(t) { navigator.clipboard.writeText(t||'').then(()=>toast.success('Copied!')).catch(()=>{}); }

function Avatar({ b, size=32 }) {
  const border = getBorderColor(b);
  const st = { width:size, height:size, borderRadius:'50%', border:`2.5px solid ${border}`, flexShrink:0, objectFit:'cover', cursor:'pointer' };
  return b.customer_fb_profile_pic
    ? <img src={b.customer_fb_profile_pic} alt="" style={st} onClick={()=>copyText(b.customer_name)} title="Copy name" />
    : <div style={{...st, background:'linear-gradient(135deg,#7c6af5,#c084fc)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:size*0.38, fontWeight:700, color:'#fff'}}
        onClick={()=>copyText(b.customer_name)} title="Copy name">{(b.customer_name||'?')[0].toUpperCase()}</div>;
}

const STATUS_COLORS = {
  pending:   { bg:'#fef3c7', text:'#92400e', label:'Pending' },
  printed:   { bg:'#dbeafe', text:'#1d4ed8', label:'Printed' },
  packed:    { bg:'#e0f2fe', text:'#0369a1', label:'Packed' },
  shipped:   { bg:'#dcfce7', text:'#15803d', label:'Shipped' },
  delivered: { bg:'#f0fdf4', text:'#16a34a', label:'Delivered' },
  cancelled: { bg:'#fee2e2', text:'#dc2626', label:'Cancelled' },
};

const DELIVERY_LABELS = { VET:'VET', JT_COD:'J&T COD', JT_NON_COD:'J&T Non COD', DOM:'DOM', SONA:'Sona', TAKE_SHOP:'Take Shop', GRAB:'Grab' };
const PROVINCE_TABS = ['VET','JT_NON_COD'];

// ── Status Update Modal ───────────────────────────────────────────────────────
function StatusModal({ bill, onClose, onUpdated }) {
  const [status, setStatus] = useState(bill.status);
  const [tracking, setTracking] = useState(bill.tracking_number||'');
  const [cancelReason, setCancelReason] = useState('');
  const [saving, setSaving] = useState(false);
  const inp = { background:'#f8f9fa', border:'1px solid #dee2e6', borderRadius:6, padding:'7px 10px', fontSize:13, width:'100%', boxSizing:'border-box', outline:'none' };

  const FLOW = ['pending','printed','packed','shipped','delivered'];

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/billing/bills/${bill.id}/status`, { status, tracking_number: tracking, cancel_reason: cancelReason });
      toast.success(`Status updated to ${status}`);
      onUpdated();
    } catch(e) { toast.error(e.response?.data?.detail||'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:600, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:12, padding:24, width:380, boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <div style={{ fontWeight:700, fontSize:15, color:'#111827' }}>Update Status — {bill.bill_number}</div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af' }}><X size={16}/></button>
        </div>

        {/* Status flow buttons */}
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:16 }}>
          {FLOW.map(s => {
            const sc = STATUS_COLORS[s];
            const isActive = status === s;
            return (
              <button key={s} onClick={()=>setStatus(s)}
                style={{ background:isActive?sc.bg:'#f3f4f6', color:isActive?sc.text:'#6b7280', border:`1px solid ${isActive?sc.text+'40':'#e5e7eb'}`, borderRadius:6, padding:'5px 12px', fontSize:12, fontWeight:isActive?700:400, cursor:'pointer' }}>
                {sc.label}
              </button>
            );
          })}
          <button onClick={()=>setStatus('cancelled')}
            style={{ background:status==='cancelled'?'#fee2e2':'#f3f4f6', color:status==='cancelled'?'#dc2626':'#6b7280', border:`1px solid ${status==='cancelled'?'#fca5a5':'#e5e7eb'}`, borderRadius:6, padding:'5px 12px', fontSize:12, fontWeight:status==='cancelled'?700:400, cursor:'pointer' }}>
            Cancel
          </button>
        </div>

        {status === 'shipped' && (
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:11, fontWeight:600, color:'#6b7280', display:'block', marginBottom:4 }}>Tracking Number</label>
            <input value={tracking} onChange={e=>setTracking(e.target.value)} style={inp} placeholder="Optional..." />
          </div>
        )}
        {status === 'cancelled' && (
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:11, fontWeight:600, color:'#6b7280', display:'block', marginBottom:4 }}>Cancel Reason *</label>
            <input value={cancelReason} onChange={e=>setCancelReason(e.target.value)} style={inp} placeholder="Reason..." />
          </div>
        )}

        <div style={{ display:'flex', gap:10 }}>
          <button onClick={onClose} style={{ flex:1, background:'#f3f4f6', color:'#374151', border:'none', borderRadius:8, padding:'9px 0', fontSize:13, cursor:'pointer' }}>Cancel</button>
          <button onClick={save} disabled={saving||(status==='cancelled'&&!cancelReason)}
            style={{ flex:1, background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:'9px 0', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            {saving?'Saving...':'Update'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Bill Detail Panel ─────────────────────────────────────────────────────────
function BillDetail({ bill, onClose, onUpdated }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStatus, setShowStatus] = useState(false);
  const [editFee, setEditFee] = useState(false);
  const [fee, setFee] = useState(bill.delivery_fee);
  const [discount, setDiscount] = useState(bill.discount);
  const [tracking, setTracking] = useState(bill.tracking_number||'');
  const [note, setNote] = useState(bill.staff_note||'');

  useEffect(() => {
    api.get(`/billing/bills/${bill.id}`).then(r=>{ setDetail(r.data); setLoading(false); }).catch(()=>setLoading(false));
  }, [bill.id]);

  const saveFee = async () => {
    try {
      await api.put(`/billing/bills/${bill.id}/edit`, { delivery_fee: parseFloat(fee), discount: parseFloat(discount), tracking_number: tracking, staff_note: note });
      toast.success('Updated'); setEditFee(false); onUpdated();
    } catch { toast.error('Failed'); }
  };

  const sc = STATUS_COLORS[bill.status] || STATUS_COLORS.pending;
  const inp = { background:'#f8f9fa', border:'1px solid #dee2e6', borderRadius:6, padding:'6px 9px', fontSize:12, outline:'none' };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:500, display:'flex', alignItems:'flex-start', justifyContent:'flex-end' }}>
      <div style={{ background:'#fff', width:420, height:'100vh', overflowY:'auto', boxShadow:'-4px 0 30px rgba(0,0,0,0.15)', display:'flex', flexDirection:'column' }}>
        {/* Header */}
        <div style={{ padding:'16px 20px', borderBottom:'1px solid #e5e7eb', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
          <div>
            <div style={{ fontFamily:'monospace', fontWeight:700, fontSize:15, color:'#7c6af5', cursor:'pointer' }} onClick={()=>copyText(bill.bill_number)}>{bill.bill_number}</div>
            <div style={{ fontSize:11, color:'#9ca3af', marginTop:1 }}>{bill.created_at}</div>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:10, background:sc.bg, color:sc.text }}>{sc.label}</span>
            <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af' }}><X size={18}/></button>
          </div>
        </div>

        <div style={{ padding:20, flex:1 }}>
          {/* Customer */}
          <div style={{ display:'flex', gap:12, alignItems:'center', marginBottom:16, background:'#f9fafb', borderRadius:8, padding:'12px 14px' }}>
            <Avatar b={bill} size={40} />
            <div>
              <div style={{ fontWeight:700, fontSize:14, color:'#111827' }}>{bill.customer_name}</div>
              {(bill.customer_phones?.length?bill.customer_phones:[bill.customer_phone].filter(Boolean)).map((p,i)=>(
                <div key={i} style={{ fontSize:12, color:'#6b7280' }}>📞 {p}</div>
              ))}
              {bill.customer_address && <div style={{ fontSize:12, color:'#6b7280' }}>📍 {bill.customer_address}</div>}
              {bill.preferred_delivery && <div style={{ fontSize:11, color:'#0369a1', marginTop:2 }}>🚚 {DELIVERY_LABELS[bill.preferred_delivery]||bill.preferred_delivery}</div>}
            </div>
          </div>

          {/* Orders */}
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:600, color:'#6b7280', marginBottom:8, textTransform:'uppercase' }}>Orders</div>
            {loading ? <div style={{ color:'#9ca3af', fontSize:12 }}>Loading...</div>
            : detail?.orders?.map(o => (
              <div key={o.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', borderBottom:'1px solid #f3f4f6', fontSize:12 }}>
                <div>
                  <span style={{ fontFamily:'monospace', color:'#7c6af5', fontWeight:700, fontSize:11 }}>{o.order_number}</span>
                  {o.product_code && <span style={{ marginLeft:8, color:'#374151' }}>{o.product_code} x{o.quantity}</span>}
                  {o.remarks && <div style={{ color:'#9ca3af', fontSize:11 }}>{o.remarks}</div>}
                </div>
                <span style={{ fontWeight:700, color:'#16a34a' }}>${o.total.toFixed(2)}</span>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div style={{ background:'#f9fafb', borderRadius:8, padding:'12px 14px', marginBottom:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'#6b7280', marginBottom:4 }}>
              <span>Subtotal</span><span>${bill.subtotal.toFixed(2)}</span>
            </div>
            {editFee ? (
              <div style={{ marginTop:8 }}>
                <div style={{ display:'flex', gap:8, marginBottom:6 }}>
                  <div style={{ flex:1 }}>
                    <label style={{ fontSize:11, color:'#6b7280', display:'block', marginBottom:2 }}>Delivery Fee</label>
                    <input type="number" step="0.5" value={fee} onChange={e=>setFee(e.target.value)} style={{...inp,width:'100%'}} />
                  </div>
                  <div style={{ flex:1 }}>
                    <label style={{ fontSize:11, color:'#6b7280', display:'block', marginBottom:2 }}>Discount</label>
                    <input type="number" step="0.5" value={discount} onChange={e=>setDiscount(e.target.value)} style={{...inp,width:'100%'}} />
                  </div>
                </div>
                <div style={{ marginBottom:6 }}>
                  <label style={{ fontSize:11, color:'#6b7280', display:'block', marginBottom:2 }}>Tracking</label>
                  <input value={tracking} onChange={e=>setTracking(e.target.value)} style={{...inp,width:'100%'}} placeholder="Tracking number..." />
                </div>
                <div style={{ marginBottom:8 }}>
                  <label style={{ fontSize:11, color:'#6b7280', display:'block', marginBottom:2 }}>Staff Note</label>
                  <input value={note} onChange={e=>setNote(e.target.value)} style={{...inp,width:'100%'}} placeholder="Note..." />
                </div>
                <div style={{ display:'flex', gap:6 }}>
                  <button onClick={saveFee} style={{ background:'#7c6af5', color:'#fff', border:'none', borderRadius:6, padding:'5px 12px', fontSize:12, cursor:'pointer' }}>✓ Save</button>
                  <button onClick={()=>setEditFee(false)} style={{ background:'#f3f4f6', color:'#374151', border:'none', borderRadius:6, padding:'5px 12px', fontSize:12, cursor:'pointer' }}>✕</button>
                </div>
              </div>
            ) : (
              <>
                {bill.delivery_fee>0&&<div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'#6b7280', marginBottom:4 }}><span>Delivery</span><span>+${bill.delivery_fee.toFixed(2)}</span></div>}
                {bill.discount>0&&<div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'#dc2626', marginBottom:4 }}><span>Discount</span><span>-${bill.discount.toFixed(2)}</span></div>}
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:15, fontWeight:700, color:'#111827', borderTop:'1px solid #e5e7eb', paddingTop:8, marginTop:4 }}>
                  <span>Total</span><span>${bill.total.toFixed(2)}</span>
                </div>
                {bill.tracking_number&&<div style={{ fontSize:11, color:'#6b7280', marginTop:6 }}>📦 Tracking: {bill.tracking_number}</div>}
                {bill.staff_note&&<div style={{ fontSize:11, color:'#6b7280', marginTop:4 }}>📝 {bill.staff_note}</div>}
                <button onClick={()=>setEditFee(true)} style={{ marginTop:8, background:'none', border:'none', color:'#7c6af5', fontSize:12, cursor:'pointer', padding:0 }}>✏️ Edit fee/discount/tracking</button>
              </>
            )}
          </div>

          {/* Timeline */}
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:600, color:'#6b7280', marginBottom:8, textTransform:'uppercase' }}>Timeline</div>
            {[['Created',bill.created_at],['Printed',bill.printed_at],['Packed',bill.packed_at],['Shipped',bill.shipped_at],['Delivered',bill.delivered_at]].map(([label,time])=>time?(
              <div key={label} style={{ display:'flex', gap:10, fontSize:12, marginBottom:4 }}>
                <span style={{ color:'#9ca3af', width:60 }}>{label}</span>
                <span style={{ color:'#374151' }}>{time}</span>
              </div>
            ):null)}
            {bill.cancel_reason&&<div style={{ fontSize:12, color:'#dc2626', marginTop:4 }}>❌ {bill.cancel_reason}</div>}
          </div>
        </div>

        {/* Footer actions */}
        <div style={{ padding:'14px 20px', borderTop:'1px solid #e5e7eb', display:'flex', gap:10 }}>
          <button onClick={()=>setShowStatus(true)}
            style={{ flex:1, background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:'10px 0', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            Update Status
          </button>
          {PROVINCE_TABS.includes(bill.preferred_delivery) && (
            <button onClick={()=>toast('Province DM coming soon')}
              style={{ flex:1, background:'#eff6ff', color:'#2563eb', border:'1px solid #bfdbfe', borderRadius:8, padding:'10px 0', fontSize:13, fontWeight:600, cursor:'pointer' }}>
              📨 Payment DM
            </button>
          )}
        </div>
      </div>
      {showStatus && <StatusModal bill={bill} onClose={()=>setShowStatus(false)} onUpdated={()=>{ setShowStatus(false); onUpdated(); }} />}
    </div>
  );
}

// ── Main Bills Page ───────────────────────────────────────────────────────────
export default function Bills() {
  const [bills, setBills] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const [filterStatus, setFilterStatus] = useState('');
  const [filterDelivery, setFilterDelivery] = useState('');
  const [filterQ, setFilterQ] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  useEffect(() => { load(); }, [page]);

  async function load(params={}) {
    setLoading(true);
    try {
      const r = await api.get('/billing/bills', { params: {
        page, per_page: 50,
        status: params.status ?? filterStatus,
        search: params.search ?? filterQ,
        delivery: params.delivery ?? filterDelivery,
        date_from: params.date_from ?? filterDateFrom,
        date_to: params.date_to ?? filterDateTo,
      }});
      setBills(r.data.bills||[]);
      setTotal(r.data.total||0);
      setPages(r.data.pages||1);
    } catch { toast.error('Failed to load bills'); }
    finally { setLoading(false); }
  }

  function search() { setPage(1); load(); }

  const inp = { background:'#f8f9fa', border:'1px solid #dee2e6', borderRadius:6, padding:'6px 10px', fontSize:13, outline:'none' };

  return (
    <div style={{ padding:24, minHeight:'100vh', background:'#f8fafc' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h1 style={{ margin:0, fontSize:22, fontWeight:700, color:'#111827' }}>🧾 Bills</h1>
          <p style={{ margin:'2px 0 0', fontSize:13, color:'#6b7280' }}>{total.toLocaleString()} bills</p>
        </div>
      </div>

      {/* Filters */}
      <div style={{ background:'#fff', borderRadius:8, border:'1px solid #e5e7eb', padding:'12px 14px', marginBottom:16, display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>
        <div style={{ position:'relative', flex:'1 1 180px' }}>
          <Search size={13} style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color:'#9ca3af' }} />
          <input value={filterQ} onChange={e=>setFilterQ(e.target.value)} onKeyDown={e=>e.key==='Enter'&&search()}
            placeholder="Bill no, customer name, phone..." style={{ ...inp, paddingLeft:28, width:'100%' }} />
        </div>
        <div style={{ flex:'0 0 130px' }}>
          <div style={{ fontSize:11, fontWeight:600, color:'#6b7280', marginBottom:3 }}>STATUS</div>
          <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{ ...inp, width:'100%' }}>
            <option value="">All</option>
            {Object.entries(STATUS_COLORS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div style={{ flex:'0 0 150px' }}>
          <div style={{ fontSize:11, fontWeight:600, color:'#6b7280', marginBottom:3 }}>DELIVERY</div>
          <select value={filterDelivery} onChange={e=>setFilterDelivery(e.target.value)} style={{ ...inp, width:'100%' }}>
            <option value="">All</option>
            {Object.entries(DELIVERY_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div style={{ flex:'0 0 130px' }}>
          <div style={{ fontSize:11, fontWeight:600, color:'#6b7280', marginBottom:3 }}>DATE FROM</div>
          <input type="date" value={filterDateFrom} onChange={e=>setFilterDateFrom(e.target.value)} style={{ ...inp, width:'100%' }} />
        </div>
        <div style={{ flex:'0 0 130px' }}>
          <div style={{ fontSize:11, fontWeight:600, color:'#6b7280', marginBottom:3 }}>TO</div>
          <input type="date" value={filterDateTo} onChange={e=>setFilterDateTo(e.target.value)} style={{ ...inp, width:'100%' }} />
        </div>
        <button onClick={search} style={{ background:'#2563eb', color:'#fff', border:'none', borderRadius:6, padding:'7px 18px', fontSize:13, fontWeight:600, cursor:'pointer' }}>Search</button>
        {(filterQ||filterStatus||filterDelivery||filterDateFrom||filterDateTo) && (
          <button onClick={()=>{ setFilterQ(''); setFilterStatus(''); setFilterDelivery(''); setFilterDateFrom(''); setFilterDateTo(''); setPage(1); load({status:'',search:'',delivery:'',date_from:'',date_to:''}); }}
            style={{ background:'#fee2e2', color:'#dc2626', border:'none', borderRadius:6, padding:'7px 12px', fontSize:13, cursor:'pointer' }}>✕ Clear</button>
        )}
      </div>

      {/* Table */}
      <div style={{ background:'#fff', borderRadius:8, border:'1px solid #e5e7eb', overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:'#f9fafb', borderBottom:'1px solid #e5e7eb' }}>
              {['Bill No','Customer','Delivery','Subtotal','Fee','Total','Status','Created','Action'].map(h=>(
                <th key={h} style={{ padding:'10px 12px', textAlign:'left', fontWeight:600, color:'#374151', fontSize:12, whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} style={{ padding:40, textAlign:'center', color:'#9ca3af' }}>Loading...</td></tr>}
            {!loading && bills.length===0 && <tr><td colSpan={9} style={{ padding:40, textAlign:'center', color:'#9ca3af' }}>No bills found</td></tr>}
            {bills.map(bill => {
              const sc = STATUS_COLORS[bill.status]||STATUS_COLORS.pending;
              return (
                <tr key={bill.id} style={{ borderBottom:'1px solid #f3f4f6', cursor:'pointer' }}
                  onMouseEnter={e=>e.currentTarget.style.background='#fafafa'}
                  onMouseLeave={e=>e.currentTarget.style.background=''}
                  onClick={()=>setSelected(bill)}>
                  <td style={{ padding:'8px 12px' }}>
                    <div style={{ fontFamily:'monospace', fontWeight:700, color:'#7c6af5', fontSize:12 }}>{bill.bill_number}</div>
                    {bill.tracking_number&&<div style={{ fontSize:10, color:'#9ca3af' }}>📦 {bill.tracking_number}</div>}
                  </td>
                  <td style={{ padding:'8px 12px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <Avatar b={bill} size={30} />
                      <div>
                        <div style={{ fontSize:13, fontWeight:500, color:'#111827' }}>{bill.customer_name}</div>
                        <div style={{ fontSize:11, color:'#6b7280' }}>
                          {bill.customer_phones?.length?bill.customer_phones[0]:bill.customer_phone||''}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding:'8px 12px' }}>
                    <span style={{ fontSize:11, background:'#e0f2fe', color:'#0369a1', padding:'2px 7px', borderRadius:8, fontWeight:600 }}>
                      {DELIVERY_LABELS[bill.preferred_delivery]||bill.delivery_category||'—'}
                    </span>
                  </td>
                  <td style={{ padding:'8px 12px', fontWeight:600, color:'#374151' }}>${bill.subtotal.toFixed(2)}</td>
                  <td style={{ padding:'8px 12px', color:'#6b7280' }}>${bill.delivery_fee.toFixed(2)}</td>
                  <td style={{ padding:'8px 12px', fontWeight:700, color:'#16a34a' }}>${bill.total.toFixed(2)}</td>
                  <td style={{ padding:'8px 12px' }}>
                    <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:10, background:sc.bg, color:sc.text }}>{sc.label}</span>
                  </td>
                  <td style={{ padding:'8px 12px', fontSize:11, color:'#9ca3af', whiteSpace:'nowrap' }}>{bill.created_at}</td>
                  <td style={{ padding:'8px 12px' }}>
                    <button onClick={e=>{e.stopPropagation();setSelected(bill);}}
                      style={{ background:'#eff6ff', color:'#2563eb', border:'none', borderRadius:6, padding:'4px 10px', fontSize:12, cursor:'pointer' }}>View</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {pages>1&&(
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:12, padding:14, borderTop:'1px solid #e5e7eb' }}>
            <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} style={{ background:'none', border:'1px solid #d1d5db', borderRadius:6, padding:'6px 8px', cursor:'pointer' }}><ChevronLeft size={14}/></button>
            <span style={{ fontSize:13, color:'#6b7280' }}>Page {page} of {pages}</span>
            <button onClick={()=>setPage(p=>Math.min(pages,p+1))} disabled={page===pages} style={{ background:'none', border:'1px solid #d1d5db', borderRadius:6, padding:'6px 8px', cursor:'pointer' }}><ChevronRight size={14}/></button>
          </div>
        )}
      </div>

      {selected && <BillDetail bill={selected} onClose={()=>setSelected(null)} onUpdated={()=>{ setSelected(null); load(); }} />}
    </div>
  );
}
